import { useEffect, useState } from 'react'

// Search progress: four rows while the search runs, one collapsed block after it finishes. His
// original requirement, literal — "Add search progress statuses; after search is complete,
// collapse all statuses and display just 'Analysis Complete (Top 48 Comps)'". The collapsed
// content was later revised to two rows of text; see the comment on the return block below.
//
// Two decisions are recorded here rather than in the MR file alone, because both are visible on the
// screen and neither is obvious from the design.
//
// **The counts are gone from rows two and three.** The mock draws `Scoring 52 / 156 Comps` and
// `Analyzing Top 48 Comps`. Those numbers cannot be known while the search runs: `POST
// /:id/arv/search` is one request that returns once, and nothing streams out of it — the pipeline
// finishes the RentCast queries, the enrichment and the ranking before the response is assembled.
// Putting 52 / 156 on screen would be a number nobody can check, in the one place the reader has no
// way to verify it, and the alternative — a progress channel — is an architecture this line of the
// estimate does not carry. So the rows name the phase and stop there. The collapsed row does carry
// a real number, because by then there is one.
//
// **The phases are timed, not reported.** Same reason. The boundaries below come from the measured
// runs in files/sow2/arv-timing.21.08.md rather than being picked to look plausible, and the last
// row deliberately never advances on its own: it holds until the response actually lands, so the
// indicator can run long but can never claim to be finished before the work is.
const PHASES = [
  'Searching Comparables',
  'Scoring Comps',
  'Analyzing Top Comps',
  'Building Your Analysis',
]

// Milliseconds from the click at which each row becomes the active one.
//
// The whole search measured 25.3-52.0 s across five addresses on 21.08, of which RentCast's two
// queries were 11.6-17.0 s. mr-03 then narrowed enrichment to a ten-row window, which is most of
// what the rest of that time was, and the searches measured since run 17-27 s.
//
// The last boundary is set against the FASTEST of that range, not the middle of it. At 20000 the
// fourth row was unreachable on a 17-19.9 s search: it would sit in the not-started style for the
// whole run and then be replaced by the collapsed pill, having never once been the active row — a
// phase drawn in the design that some searches never show. 15000 leaves it about two seconds of the
// quickest run, and a long search sits on it, which is the row that is meant to hold.
const PHASE_STARTS_MS = [0, 5000, 10000, 15000]

const TICK_MS = 500

// The `self-*` class is NOT in here, and that is deliberate — each branch below adds its own,
// because the two want opposite edges and a shared one silently wins over the parent.
//
// Something has to stop these stretching: they hug their text — 171 / 172 / 178 / 163 for the four
// rows and 241 for the collapsed one, all different, which is what says the width follows the
// string. Dropped into a flex column an inline-flex child still stretches to the line: measured
// 1200px against a drawn 241 before `self-start` was added here.
//
// It then stayed on the shared class through ARV 5, which right-aligned the four rows with
// `items-end` on their stack — and `align-self` beats `align-item`s, so the stack was right-aligned
// and every pill in it was not. Measured 02.09 at 1280 mid-search: four left edges all on 1052.56,
// four right edges ragged across 1191.33 to 1240. No test could see it; jsdom has no layout engine,
// and the test named for that requirement asserted `items-end` on the parent, which was true.
const pillCls = 'inline-flex items-center justify-center h-[23px] rounded-[3.5px] border px-[23.5px] text-[13px] whitespace-nowrap'
const REACHED = '#4CBC18'
const AHEAD = '#C9D5C4'

// Comment Set #2, items 3.1-3.2: background is #E9F0E5 (client-confirmed) and the collapsed
// "Analysis Complete" pill now wraps in this same module too. Border-token and checkmark
// history: see README.md.
const moduleCls = 'rounded-[8px] border border-brand-fieldBdr bg-[#E9F0E5] p-[10px]'

// The collapsed block's border, background and horizontal padding carry over from the single-line
// pill it replaces; the fixed 23px height cannot, now that it holds two lines, so vertical padding
// takes the module's own 10px instead of an invented number.
const collapsedCls = 'inline-flex flex-col items-start gap-y-[2px] rounded-[3.5px] border px-[23.5px] py-[10px]'

export default function SearchProgress({ searching, hasSearched, compCount }) {
  const [elapsed, setElapsed] = useState(0)
  const [wasSearching, setWasSearching] = useState(searching)

  // The reset is adjusted during render, not in the effect below, and that is the whole point.
  // React runs a passive effect after paint, so with `setElapsed(0)` living there the first frame
  // of a SECOND search still held the first one's elapsed time — 25000 after a 25-second search —
  // and all four rows painted as reached before the reset landed. React's own documented way out
  // of that is comparing the previous prop during render; no test can see the difference, because
  // act() flushes passive effects and the intermediate commit is never observable from jsdom.
  if (searching !== wasSearching) {
    setWasSearching(searching)
    if (searching) setElapsed(0)
  }

  useEffect(() => {
    if (!searching) return undefined
    const startedAt = Date.now()
    const timer = setInterval(() => setElapsed(Date.now() - startedAt), TICK_MS)
    return () => clearInterval(timer)
  }, [searching])

  if (searching) {
    // The last index whose boundary has passed. findLastIndex is avoided for the same reason the
    // rest of this codebase avoids newer array methods — the CRA browser targets are set by
    // package.json, not by the machine the tests run on.
    let active = 0
    for (let i = 0; i < PHASE_STARTS_MS.length; i++) {
      if (elapsed >= PHASE_STARTS_MS[i]) active = i
    }
    return (
      // Comment Set #3 item 2 wraps the stack in moduleCls (defined above); everything about the
      // stack's own alignment below is unchanged by that.
      //
      // Right-aligned, because the export right-aligns them: four pills 171/172/178/163 wide, all
      // ending on x 9881.5 (`arv_search/in_use`). Left-aligned they staggered on the wrong edge.
      //
      // `self-end` on the pill and not `items-end` alone: the parent class is what a reader edits,
      // and it cannot reach a child that sets its own alignment. Stated on the child, the edge is
      // where the pill can be seen to be, and it also does the job `self-start` used to do here —
      // an `align-self` other than `stretch` is what keeps the width on the text.
      <div className={moduleCls}>
        <div className="flex flex-col items-end gap-y-[9px]" role="status" aria-live="polite">
          {PHASES.map((label, i) => (
            <span
              key={label}
              className={`${pillCls} self-end`}
              style={{
                background: '#F4FAEF',
                borderColor: i <= active ? REACHED : AHEAD,
                color: i <= active ? '#17270D' : '#3E4A3C',
              }}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    )
  }

  if (!hasSearched) return null

  // Two rows, not one pill: no Figma frame exists for this state, so bold 13px / regular 12px is
  // his own written spec, taken literally. `compCount` is the caller's count, and the caller
  // (pages/Arv.jsx) now passes the Show field's own filtered total rather than the raw search
  // result count, so the two numbers on screen always agree.
  //
  // `self-start` on the wrapper is a no-op today (moduleCls is not a flex container) but costs
  // nothing to keep — it is what protects the next call site that drops this back into a flex
  // column, which is exactly how it broke the first time.
  return (
    <div className={moduleCls}>
      <div
        className={`${collapsedCls} self-start`}
        style={{ background: '#F4FAEF', borderColor: REACHED }}
      >
        <span className="text-[13px] font-bold text-brand-dark">Analysis Complete</span>
        <span className="text-[12px] font-normal text-brand-secondary">
          Found {compCount} Relevant {compCount === 1 ? 'Comp' : 'Comps'}
        </span>
      </div>
    </div>
  )
}
